import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Modal } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { useSearch } from '../hooks/useSearch';
import { useFilters } from '../hooks/useFilters';
import { usePagination } from '../hooks/usePagination';
import { ColumnCustomizer } from '../components/ColumnCustomizer';
import { MultiSelect } from '../components/ui/MultiSelect';
import { Textarea } from '../components/ui/Textarea';
import { Tooltip } from '../components/ui/Tooltip';
import { DatePicker } from '../components/ui/DatePicker';
import { Plus, Filter, Download, Eye, Edit, Search, PhoneCall, User, Upload, Trash2, Ban, AlertTriangle, Save, CheckCircle2, X, CalendarClock, Calendar, Clock } from 'lucide-react';
import { useNotifications } from '../contexts/NotificationContext';
import { motion, AnimatePresence } from 'framer-motion';
// Mock data
const initialLeadsData = Array.from({
  length: 50
}, (_, i) => ({
  id: String(i + 1),
  name: `Azienda Lead ${i} SPA`,
  contact: `Referente ${i}`,
  phone: `333 ${1000000 + i}`,
  email: `info@lead${i}.it`,
  status: i % 5 === 0 ? 'Positivo' : i % 5 === 1 ? 'Negativo' : i % 5 === 2 ? 'In lavorazione' : i % 5 === 3 ? 'Da ricontattare' : 'Da contattare',
  source: i % 4 === 0 ? 'Fiera' : i % 4 === 1 ? 'Web' : i % 4 === 2 ? 'Passaparola' : 'Altro',
  services: ['Consulenza'],
  notes: 'Interessati a sicurezza',
  lastContact: '2024-05-10',
  assignedTo: i % 3 === 0 ? 'Alessio' : i % 3 === 1 ? 'Lorenzo' : 'Gian Franco',
  referent: `Referente Interno ${i % 4}`,
  // New fields for specific statuses
  callbackDate: i % 5 === 3 ? '2024-06-15' : '',
  callbackTime: i % 5 === 3 ? '10:00' : '',
  callbackNote: i % 5 === 3 ? 'Richiamare per conferma' : '',
  appointmentDate: i % 5 === 2 ? '2024-06-20' : '',
  appointmentTime: i % 5 === 2 ? '14:30' : '',
  appointmentNote: i % 5 === 2 ? 'Incontro preliminare' : ''
}));
const blacklistData = [{
  id: '1',
  name: 'Spam Company SRL',
  phone: '0000000000',
  reason: 'Non interessato',
  date: '2024-01-01',
  by: 'Admin'
}];
const columnsConfig = [{
  key: 'name',
  label: 'Ragione sociale'
}, {
  key: 'contact',
  label: 'Referente'
}, {
  key: 'phone',
  label: 'Telefono'
}, {
  key: 'source',
  label: 'Provenienza'
}, {
  key: 'status',
  label: 'Stato'
}, {
  key: 'lastContact',
  label: 'Ultimo contatto'
}, {
  key: 'assignedTo',
  label: 'Assegnato a'
}, {
  key: 'actions',
  label: 'Azioni'
}];
const kanbanColumns = [{
  id: 'Da contattare',
  title: 'Da contattare',
  color: 'bg-slate-50 border-slate-200'
}, {
  id: 'Da ricontattare',
  title: 'Da ricontattare',
  color: 'bg-yellow-50 border-yellow-200'
}, {
  id: 'In lavorazione',
  title: 'In lavorazione',
  color: 'bg-blue-50 border-blue-200'
}, {
  id: 'Positivo',
  title: 'Positivo',
  color: 'bg-green-50 border-green-200'
}, {
  id: 'Negativo',
  title: 'Negativo',
  color: 'bg-red-50 border-red-200'
}];
export function CommercialPage() {
  const [viewMode, setViewMode] = useState<'list' | 'kanban' | 'blacklist'>('kanban');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isBlacklistModalOpen, setIsBlacklistModalOpen] = useState(false);
  const [isCallbackModalOpen, setIsCallbackModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(columnsConfig.map(c => c.key));
  const [leadsData, setLeadsData] = useState(initialLeadsData);
  const [draggedCard, setDraggedCard] = useState<any>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [callbackSaveSuccess, setCallbackSaveSuccess] = useState(false);
  const [formData, setFormData] = useState<any>({});
  const {
    addNotification
  } = useNotifications();
  const {
    filters,
    setFilter,
    clearFilters,
    filteredData: filterResult
  } = useFilters(leadsData);
  const {
    searchTerm,
    setSearchTerm,
    filteredData: searchResult
  } = useSearch(filterResult, ['name', 'contact', 'phone']);
  const {
    currentPage,
    totalPages,
    paginatedData,
    goToPage,
    pageSize,
    setPageSize,
    totalItems,
    setCurrentPage
  } = usePagination(searchResult);
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm]);
  const handleView = (item: any) => {
    setSelectedItem(item);
    setIsViewModalOpen(true);
  };
  const handleEdit = (item: any) => {
    setSelectedItem(item);
    setFormData(item);
    setIsModalOpen(true);
  };
  const handleCreate = () => {
    setSelectedItem(null);
    setFormData({
      status: 'Da contattare',
      services: []
    });
    setIsModalOpen(true);
  };
  const handleDelete = (item: any) => {
    setSelectedItem(item);
    setIsBlacklistModalOpen(true);
  };
  const handleCallback = (item: any) => {
    setSelectedItem(item);
    setFormData({
      ...item,
      callbackDate: item.callbackDate || '',
      callbackTime: item.callbackTime || '',
      callbackNote: item.callbackNote || ''
    });
    setIsCallbackModalOpen(true);
  };
  const handleSave = () => {
    setSaveSuccess(true);
    // Update local state to reflect changes immediately
    if (selectedItem) {
      setLeadsData(prev => prev.map(item => item.id === selectedItem.id ? formData : item));
    } else {
      // Create new
      const newItem = {
        ...formData,
        id: String(leadsData.length + 1),
        lastContact: new Date().toISOString().split('T')[0]
      };
      setLeadsData([...leadsData, newItem]);
    }
    setTimeout(() => {
      setSaveSuccess(false);
      setIsModalOpen(false);
    }, 1500);
  };
  const handleCallbackSave = () => {
    setCallbackSaveSuccess(true);
    // Update local state to reflect callback changes
    if (selectedItem) {
      setLeadsData(prev => prev.map(item => item.id === selectedItem.id ? {
        ...item,
        callbackDate: formData.callbackDate,
        callbackTime: formData.callbackTime,
        callbackNote: formData.callbackNote
      } : item));
    }
    setTimeout(() => {
      setCallbackSaveSuccess(false);
      setIsCallbackModalOpen(false);
    }, 1500);
  };
  const confirmBlacklist = () => {
    addNotification('success', 'Lead eliminato', `Lead ${selectedItem.name} aggiunto alla blacklist.`);
    setIsBlacklistModalOpen(false);
  };
  const handleUpload = () => {
    setTimeout(() => {
      addNotification('success', 'Importazione completata', '5 lead importati con successo. 1 duplicato ignorato.');
      setIsUploadModalOpen(false);
    }, 1500);
  };
  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, card: any) => {
    setDraggedCard(card);
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  const handleDrop = (e: React.DragEvent, targetStatus: string) => {
    e.preventDefault();
    if (draggedCard && draggedCard.status !== targetStatus) {
      setLeadsData(prev => prev.map(item => item.id === draggedCard.id ? {
        ...item,
        status: targetStatus
      } : item));
      addNotification('info', 'Stato aggiornato', `Lead spostato in "${targetStatus}"`);
    }
    setDraggedCard(null);
  };
  const hasActiveFilters = Object.keys(filters).some(k => filters[k]);
  return <Layout data-id="element-3694" data-mp="2av6gvm2ag77n">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4" data-id="element-3695" data-mp="2fwccbrnc28kv">
        <div data-id="element-3696" data-mp="20z3fiif9gclr">
          <h1 className="text-3xl font-bold text-seform-navy" data-id="element-3697" data-mp="20zlcxidnswv9">Commerciale</h1>
          <p className="text-slate-500 mt-1" data-id="element-3698" data-mp="25y6atb5fv4gj">
            Gestione pipeline, lead e contatti.
          </p>
        </div>
        <div className="flex gap-3" data-id="element-3699" data-mp="pjeu3vhnnln2">
          <div className="bg-slate-100 p-1 rounded-xl flex items-center" data-id="element-3700" data-mp="pid8ix30jgr3">
            <button onClick={() => setViewMode('kanban')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${viewMode === 'kanban' ? 'bg-white text-seform-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-3701" data-mp="uk2jkcq8juf6">
              Pipeline
            </button>
            <button onClick={() => setViewMode('list')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${viewMode === 'list' ? 'bg-white text-seform-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-3702" data-mp="3hqx8o8lgbskm">
              Lista
            </button>
            <button onClick={() => setViewMode('blacklist')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${viewMode === 'blacklist' ? 'bg-white text-seform-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-3703" data-mp="2l30xxkcp3rhb">
              Blacklist
            </button>
          </div>
          <Button variant="flat" color="secondary" onClick={() => setIsUploadModalOpen(true)} startContent={<Upload className="h-4 w-4" data-id="element-3705" data-mp="1uottnqutsum3" />} data-id="element-3704" data-mp="22b86vvh6sgc8">
            Importa
          </Button>
          <Button onClick={handleCreate} startContent={<Plus className="h-4 w-4" data-id="element-3707" data-mp="1lnvx1tmpvpdj" />} data-id="element-3706" data-mp="1pqs911hr4n8i">
            Nuovo lead
          </Button>
        </div>
      </div>

      {viewMode !== 'blacklist' && <Card className="mb-6" noPadding data-id="element-3708" data-mp="1grx21n6vv945">
          <div className="p-4 flex flex-col gap-4" data-id="element-3709" data-mp="1bqr67ghxf5qe">
            <div className="flex flex-col sm:flex-row gap-4" data-id="element-3710" data-mp="1bskd8wwgbryx">
              <div className="flex-1" data-id="element-3711" data-mp="16yzfn5yrt009">
                <Input placeholder="Cerca lead..." startContent={<Search className="h-4 w-4" data-id="element-3713" data-mp="120xzotwn5gt0" />} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} fullWidth data-id="element-3712" data-mp="16xy3ey60q7gy" />
              </div>
              <div className="flex gap-2" data-id="element-3714" data-mp="t1ubjbimab7n">
                <Button variant={showFilters ? 'solid' : 'flat'} color="secondary" onClick={() => setShowFilters(!showFilters)} startContent={<Filter className="h-4 w-4" data-id="element-3716" data-mp="34mjz4egeftrw" />} data-id="element-3715" data-mp="o1y7xddu0lsm">
                  Filtri{' '}
                  {hasActiveFilters && <Badge variant="primary" size="sm" className="ml-1" data-id="element-3717" data-mp="39n7u1b7r0ure">
                      {Object.keys(filters).filter(k => filters[k]).length}
                    </Badge>}
                </Button>
                {viewMode === 'list' && <ColumnCustomizer columns={columnsConfig} visibleColumns={visibleColumns} onColumnChange={setVisibleColumns} pageKey="commercial" data-id="element-3718" data-mp="2lxwxy71cfojl" />}
              </div>
            </div>

            {showFilters && <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-slate-100 animate-in fade-in slide-in-from-top-2" data-id="element-3719" data-mp="1y56p46oqlldj">
                <Select label="Provenienza" value={filters.source || ''} onChange={e => setFilter('source', e.target.value)} options={[{
            value: '',
            label: 'Tutte'
          }, {
            value: 'Web',
            label: 'Web'
          }, {
            value: 'Passaparola diretto',
            label: 'Passaparola diretto'
          }, {
            value: 'Partner',
            label: 'Partner'
          }, {
            value: 'Commercialista',
            label: 'Commercialista'
          }, {
            value: 'Altro',
            label: 'Altro'
          }]} data-id="element-3720" data-mp="35j0fvnosn390" />
                <Select label="Stato" value={filters.status || ''} onChange={e => setFilter('status', e.target.value)} options={[{
            value: '',
            label: 'Tutti'
          }, {
            value: 'Da contattare',
            label: 'Da contattare'
          }, {
            value: 'Da ricontattare',
            label: 'Da ricontattare'
          }, {
            value: 'In lavorazione',
            label: 'In lavorazione'
          }, {
            value: 'Positivo',
            label: 'Positivo'
          }, {
            value: 'Negativo',
            label: 'Negativo'
          }]} data-id="element-3721" data-mp="echvnqjl52uc" />
                <Select label="Servizi" value={filters.services || ''} onChange={e => setFilter('services', e.target.value)} options={[{
            value: '',
            label: 'Tutti'
          }, {
            value: 'Consulenza',
            label: 'Consulenza'
          }, {
            value: 'Formazione',
            label: 'Formazione'
          }, {
            value: 'Altro',
            label: 'Altro'
          }]} data-id="element-3722" data-mp="3t8cto8rs5fnz" />
                <Select label="Assegnato a" value={filters.assignedTo || ''} onChange={e => setFilter('assignedTo', e.target.value)} options={[{
            value: '',
            label: 'Tutti'
          }, {
            value: 'Alessio',
            label: 'Alessio'
          }, {
            value: 'Lorenzo',
            label: 'Lorenzo'
          }, {
            value: 'Gian Franco',
            label: 'Gian Franco'
          }, {
            value: 'Tiziano',
            label: 'Tiziano'
          }]} data-id="element-3723" data-mp="ymalx03cvk1l" />
                <div className="col-span-full flex justify-end" data-id="element-3724" data-mp="1ytxvnfqfar5d">
                  <Button variant="flat" color="danger" size="sm" onClick={clearFilters} startContent={<X className="h-4 w-4" data-id="element-3726" data-mp="28swxofgonoje" />} data-id="element-3725" data-mp="1ywbnlfpt3n8p">
                    Resetta filtri
                  </Button>
                </div>
              </div>}
          </div>
        </Card>}

      {viewMode === 'kanban' && <div className="overflow-x-auto pb-4" data-id="element-3727" data-mp="1sukr0b7ay966">
          <div className="flex gap-4 min-w-max" data-id="element-3728" data-mp="1svdo0kgglkb4">
            {kanbanColumns.map(col => {
          const cards = searchResult.filter(i => i.status === col.id);
          return <div key={col.id} className={`w-72 flex-shrink-0 rounded-xl border ${col.color} flex flex-col max-h-[calc(100vh-280px)]`} onDragOver={handleDragOver} onDrop={e => handleDrop(e, col.id)} data-id="element-3729" data-mp="21t68e311fi5d">
                  <div className="p-3 border-b border-slate-200/50 flex items-center justify-between" data-id="element-3730" data-mp="26udbvpw5dv68">
                    <h3 className="font-semibold text-slate-700 text-sm" data-id="element-3731" data-mp="hffiuroejif6">
                      {col.title}
                    </h3>
                    <Badge variant="neutral" size="sm" data-id="element-3732" data-mp="2o366gvfu9ztb">
                      {cards.length}
                    </Badge>
                  </div>
                  <div className="flex-1 overflow-y-auto p-2 space-y-2" data-id="element-3733" data-mp="2t17s9o4ngd8h">
                    {cards.map(item => <div key={item.id} draggable onDragStart={e => handleDragStart(e, item)} onClick={() => handleView(item)} className={`bg-white p-3 rounded-lg border border-slate-100 shadow-sm hover:shadow-md transition-all cursor-pointer ${draggedCard?.id === item.id ? 'opacity-50' : ''}`} data-id="element-3734" data-mp="2ectlx380y6n6">
                        <div className="flex justify-between items-start mb-2" data-id="element-3735" data-mp="20dbush79ahan">
                          <span className="font-bold text-seform-navy text-sm line-clamp-1" data-id="element-3736" data-mp="20feu5ruatms1">
                            {item.name}
                          </span>
                          <Badge variant="neutral" size="sm" className="text-[10px] flex-shrink-0 ml-2" data-id="element-3737" data-mp="3ur323a244p2x">
                            {item.source}
                          </Badge>
                        </div>
                        <p className="text-xs text-slate-500 mb-1 flex items-center gap-1" data-id="element-3738" data-mp="28qbvlqed04tv">
                          <User className="h-3 w-3" data-id="element-3739" data-mp="23t3g5xkliewb" /> {item.contact}
                        </p>
                        <p className="text-xs text-slate-500 mb-2 flex items-center gap-1" data-id="element-3740" data-mp="1yvk1wxnwe24d">
                          <PhoneCall className="h-3 w-3" data-id="element-3741" data-mp="1ttmcflowmb2z" /> {item.phone}
                        </p>
                        {item.status === 'Da ricontattare' && item.callbackDate && <div className="mb-2 p-1.5 bg-yellow-50 rounded border border-yellow-100 flex items-center gap-1.5" data-id="element-3742" data-mp="1kwkcvowh3l7o">
                              <CalendarClock className="h-3 w-3 text-yellow-600" data-id="element-3743" data-mp="3f8s1kki7b5v0" />
                              <span className="text-[10px] text-yellow-700 font-medium" data-id="element-3744" data-mp="3f6nvbvwtxurk">
                                {item.callbackDate} {item.callbackTime}
                              </span>
                            </div>}
                        {item.status === 'In lavorazione' && item.appointmentDate && <div className="mb-2 p-1.5 bg-blue-50 rounded border border-blue-100 flex items-center gap-1.5" data-id="element-3745" data-mp="5aqfwkym9zgf">
                              <Calendar className="h-3 w-3 text-blue-600" data-id="element-3746" data-mp="38s8ncrah94gb" />
                              <span className="text-[10px] text-blue-700 font-medium" data-id="element-3747" data-mp="38sieii79dhnl">
                                {item.appointmentDate} {item.appointmentTime}
                              </span>
                            </div>}
                        <div className="flex justify-between items-center pt-2 border-t border-slate-50" data-id="element-3748" data-mp="t1vyh7vxz5uf">
                          <span className="text-[10px] text-slate-400" data-id="element-3749" data-mp="x1o9rscpzq3h">
                            {item.lastContact}
                          </span>
                          <div className="w-6 h-6 rounded-full bg-seform-orange/10 text-seform-orange text-xs flex items-center justify-center font-bold" data-id="element-3750" data-mp="xylm8ki4aygy">
                            {item.assignedTo.charAt(0)}
                          </div>
                        </div>
                      </div>)}
                    {cards.length === 0 && <div className="text-center py-8 text-slate-400 text-sm" data-id="element-3751" data-mp="f354e48jtf0u">
                        Nessun elemento
                      </div>}
                  </div>
                </div>;
        })}
          </div>
        </div>}

      {viewMode === 'list' && <Card noPadding className="overflow-hidden" data-id="element-3752" data-mp="sz8rldl08n3t">
          <div className="overflow-x-auto" data-id="element-3753" data-mp="1zi6y3gijkoio">
            <table className="w-full text-left border-collapse table-auto" data-id="element-3754" data-mp="1zji3tlvjs6cf">
              <thead data-id="element-3755" data-mp="24f7a0822olhp">
                <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-3756" data-mp="24ilafar6ekmi">
                  {columnsConfig.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className={`px-4 py-4 ${col.key === 'actions' ? 'text-left pr-6' : ''}`} data-id="element-3757" data-mp="3f756o2w708am">
                        {col.label}
                      </th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-3758" data-mp="317nct941i4ue">
                {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-3759" data-mp="2wbyawz4jsbux">
                    {visibleColumns.includes('name') && <td className="px-4 py-4 text-sm font-medium text-slate-900" data-id="element-3760" data-mp="ug4pluhozmsx">
                        {item.name}
                      </td>}
                    {visibleColumns.includes('contact') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-3761" data-mp="2oqumu9bd5ha8">
                        {item.contact}
                      </td>}
                    {visibleColumns.includes('phone') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-3762" data-mp="2f1b6pbf1c1j2">
                        {item.phone}
                      </td>}
                    {visibleColumns.includes('source') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-3763" data-mp="37ofgdtp2p416">
                        {item.source}
                      </td>}
                    {visibleColumns.includes('status') && <td className="px-4 py-4" data-id="element-3764" data-mp="2xsvmtdxzehfl">
                        <Badge variant={item.status === 'Positivo' ? 'success' : item.status === 'Negativo' ? 'danger' : 'info'} data-id="element-3765" data-mp="1i97ki7b6opn0">
                          {item.status}
                        </Badge>
                      </td>}
                    {visibleColumns.includes('lastContact') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-3766" data-mp="1jpleh5eyw6tn">
                        {item.lastContact}
                      </td>}
                    {visibleColumns.includes('assignedTo') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-3767" data-mp="15s6kroholcvv">
                        {item.assignedTo}
                      </td>}
                    {visibleColumns.includes('actions') && <td className="px-4 py-4 pr-6" data-id="element-3768" data-mp="dac4v5lzwfat">
                        <div className="flex items-center gap-1" data-id="element-3769" data-mp="8cal0igqwbya">
                          <Tooltip content="Vedi dettaglio" data-id="element-3770" data-mp="8c0soo889spf">
                            <Button variant="light" size="sm" className="!px-2" onClick={() => handleView(item)} data-id="element-3771" data-mp="37r2ymqa4nph2">
                              <Eye className="h-4 w-4 text-slate-500" data-id="element-3772" data-mp="3gp59dxjnxwhj" />
                            </Button>
                          </Tooltip>
                          <Tooltip content="Modifica" data-id="element-3773" data-mp="3qfq2j5gki8t2">
                            <Button variant="light" size="sm" className="!px-2" onClick={() => handleEdit(item)} data-id="element-3774" data-mp="3qj480mkj2y4q">
                              <Edit className="h-4 w-4 text-seform-orange" data-id="element-3775" data-mp="8amubbvb1gof" />
                            </Button>
                          </Tooltip>
                          {item.status === 'Da ricontattare' && <Tooltip content="Programma ricontatto" data-id="element-3776" data-mp="2a4m2hod4rhww">
                              <Button variant="light" size="sm" className="!px-2" onClick={() => handleCallback(item)} data-id="element-3777" data-mp="22jsbf63erure">
                                <CalendarClock className="h-4 w-4 text-blue-500" data-id="element-3778" data-mp="1nuvt8g7n11d2" />
                              </Button>
                            </Tooltip>}
                          <Tooltip content="Elimina" data-id="element-3779" data-mp="2gj1h3btzn6h4">
                            <Button variant="light" size="sm" className="!px-2" onClick={() => handleDelete(item)} data-id="element-3780" data-mp="2gjkviikfjcqe">
                              <Trash2 className="h-4 w-4 text-red-500" data-id="element-3781" data-mp="105kzhiopg78f" />
                            </Button>
                          </Tooltip>
                        </div>
                      </td>}
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="border-t border-slate-100" data-id="element-3782" data-mp="12hvhbyjtij1s">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-3783" data-mp="nm652s4fpka9" />
          </div>
        </Card>}

      {viewMode === 'blacklist' && <Card noPadding className="overflow-hidden" data-id="element-3784" data-mp="jlvq4pynzjj9">
          <div className="p-4 bg-red-50 border-b border-red-100 flex items-center gap-2 text-red-700 text-sm" data-id="element-3785" data-mp="4voh10ujrcv8">
            <Ban className="h-4 w-4" data-id="element-3786" data-mp="9tpyxig8l654" />I contatti in questa lista sono bloccati
            e non possono essere re-importati o creati manualmente.
          </div>
          <div className="overflow-x-auto" data-id="element-3787" data-mp="3s2i4thdw2c6u">
            <table className="w-full text-left border-collapse" data-id="element-3788" data-mp="3w1zekn0clvht">
              <thead data-id="element-3789" data-mp="3ddfg3nbn1h0t">
                <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-3790" data-mp="3dcnh02iu115s">
                  <th className="px-6 py-4" data-id="element-3791" data-mp="38fx57re28rq6">Ragione sociale</th>
                  <th className="px-6 py-4" data-id="element-3792" data-mp="3medjq4i5zmiv">Telefono</th>
                  <th className="px-6 py-4" data-id="element-3793" data-mp="3mbr199tqjmlv">Motivazione</th>
                  <th className="px-6 py-4" data-id="element-3794" data-mp="3he92gc9fmnrl">Data eliminazione</th>
                  <th className="px-6 py-4" data-id="element-3795" data-mp="6nbvntzo56h">Eliminato da</th>
                  <th className="px-6 py-4 text-right" data-id="element-3796" data-mp="4srqbqjkh3g">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-3797" data-mp="3rb3wahurfzzj">
                {blacklistData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors" data-id="element-3798" data-mp="4xlwv0px9rg2">
                    <td className="px-6 py-4 font-medium text-slate-900" data-id="element-3799" data-mp="iyg4hh55bodo">
                      {item.name}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600" data-id="element-3800" data-mp="dw7coo40esd5">
                      {item.phone}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600" data-id="element-3801" data-mp="1pc2vfrmqswaa">
                      {item.reason}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600" data-id="element-3802" data-mp="1uch4nd02yc9w">
                      {item.date}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600" data-id="element-3803" data-mp="1z9qzj868ng54">
                      {item.by}
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-3804" data-mp="16luwzmtzmu0f">
                      <Tooltip content="Ripristina lead" data-id="element-3805" data-mp="16mecyy02v8bt">
                        <Button variant="light" color="success" size="sm" data-id="element-3806" data-mp="1bkfv9gpoj3m8">
                          Ripristina
                        </Button>
                      </Tooltip>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </Card>}

      {/* View Modal */}
      <Modal isOpen={isViewModalOpen} onClose={() => setIsViewModalOpen(false)} title="Dettaglio lead" size="lg" data-id="element-3807" data-mp="ntsncvfrkdzo">
        {selectedItem && <div className="space-y-6" data-id="element-3808" data-mp="be2pg6w5byx0">
            <div className="grid grid-cols-2 gap-4" data-id="element-3809" data-mp="bawzkpfp0t08">
              <div data-id="element-3810" data-mp="6cn6ckdj42h9">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3811" data-mp="6c3o12jyvc2p">
                  Ragione sociale
                </span>
                <p className="font-medium" data-id="element-3812" data-mp="3tie56yj3z9fl">{selectedItem.name}</p>
              </div>
              <div data-id="element-3813" data-mp="3tk7c8exmvvo4">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3814" data-mp="3orxo94g2x5oh">
                  Stato
                </span>
                <div className="mt-1" data-id="element-3815" data-mp="3jtmeetpsav69">
                  <Badge variant={selectedItem.status === 'Positivo' ? 'success' : selectedItem.status === 'Negativo' ? 'danger' : 'info'} data-id="element-3816" data-mp="3jqgr9k0pu3d6">
                    {selectedItem.status}
                  </Badge>
                </div>
              </div>
              <div data-id="element-3817" data-mp="10uk30yn9wjoi">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3818" data-mp="10t8r2bjoc5mb">
                  Referente
                </span>
                <p className="font-medium" data-id="element-3819" data-mp="1eta2gkxkevg1">{selectedItem.contact}</p>
              </div>
              <div data-id="element-3820" data-mp="1aszksb0euglc">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3821" data-mp="1aqdd8bdis134">
                  Telefono
                </span>
                <p className="font-medium" data-id="element-3822" data-mp="i42d11tts26u">{selectedItem.phone}</p>
              </div>
              <div data-id="element-3823" data-mp="vv795txziccf">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3824" data-mp="vvqod2w92mmr">
                  Email
                </span>
                <p className="font-medium" data-id="element-3825" data-mp="r3pa5bfiulu3">{selectedItem.email}</p>
              </div>
              <div data-id="element-3826" data-mp="3duwirndot8s">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3827" data-mp="3dd302s3ot4k">
                  Provenienza
                </span>
                <p className="font-medium" data-id="element-3828" data-mp="3les41i0rblrf">{selectedItem.source}</p>
              </div>
              <div data-id="element-3829" data-mp="3lcxhjz9u7ll8">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3830" data-mp="3q58in5gbddfd">
                  Assegnato a
                </span>
                <p className="font-medium" data-id="element-3831" data-mp="3bhnj1lindwc7">{selectedItem.assignedTo}</p>
              </div>
              <div data-id="element-3832" data-mp="3ggqlqzb5nqh0">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3833" data-mp="3gg71vd1y2s0c">
                  Ultimo contatto
                </span>
                <p className="font-medium" data-id="element-3834" data-mp="cyymczuow48s">{selectedItem.lastContact}</p>
              </div>
            </div>

            {selectedItem.status === 'In lavorazione' && <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 mt-4" data-id="element-3835" data-mp="3u6u3bcz3aw04">
                <h4 className="text-sm font-bold text-blue-800 uppercase tracking-wider mb-3 flex items-center gap-2" data-id="element-3836" data-mp="2z7qouue7j74">
                  <Calendar className="h-4 w-4" data-id="element-3837" data-mp="2iu1eob1linoa" /> Prossimo appuntamento
                </h4>
                <div className="grid grid-cols-2 gap-4 mb-3" data-id="element-3838" data-mp="2nsba8mr8fqaf">
                  <div data-id="element-3839" data-mp="u79bci5twiba">
                    <span className="text-xs font-semibold text-blue-400 uppercase" data-id="element-3840" data-mp="u7riwaxxyiyi">
                      Data
                    </span>
                    <p className="font-medium text-blue-900" data-id="element-3841" data-mp="kihnom7swk66">
                      {selectedItem.appointmentDate || '-'}
                    </p>
                  </div>
                  <div data-id="element-3842" data-mp="fj4sne6s06si">
                    <span className="text-xs font-semibold text-blue-400 uppercase" data-id="element-3843" data-mp="akbmy7xqdk2w">
                      Ora
                    </span>
                    <p className="font-medium text-blue-900" data-id="element-3844" data-mp="6hncxwxa674c">
                      {selectedItem.appointmentTime || '-'}
                    </p>
                  </div>
                </div>
                <div data-id="element-3845" data-mp="3sqq0w6w01u1a">
                  <span className="text-xs font-semibold text-blue-400 uppercase" data-id="element-3846" data-mp="3sr82zk5cf2h4">
                    Note appuntamento
                  </span>
                  <p className="text-sm text-blue-800 mt-1" data-id="element-3847" data-mp="1qyjxf45882y6">
                    {selectedItem.appointmentNote || '-'}
                  </p>
                </div>
                <div className="mt-3 flex justify-end" data-id="element-3848" data-mp="1yhjf95d1hmff">
                  <Button size="sm" variant="flat" color="primary" onClick={() => {
              setIsViewModalOpen(false);
              handleEdit(selectedItem);
            }} data-id="element-3849" data-mp="23gnn9so89e46">
                    Modifica appuntamento
                  </Button>
                </div>
              </div>}

            {selectedItem.notes && <div data-id="element-3850" data-mp="1kk4nhtzbd2ka">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3851" data-mp="1pho1css3ndbp">
                  Note
                </span>
                <p className="text-sm text-slate-600 mt-1" data-id="element-3852" data-mp="1uhir2t051wv6">
                  {selectedItem.notes}
                </p>
              </div>}
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-3853" data-mp="30yw3t8j6qjd8">
              <Button variant="flat" color="default" onClick={() => setIsViewModalOpen(false)} data-id="element-3854" data-mp="35ttesl00z2sr">
                Chiudi
              </Button>
              <Button onClick={() => {
            setIsViewModalOpen(false);
            handleEdit(selectedItem);
          }} startContent={<Edit className="h-4 w-4" data-id="element-3856" data-mp="38ehkg4u2lzom" />} data-id="element-3855" data-mp="2zhh1wetomvt3">
                Modifica
              </Button>
            </div>
          </div>}
      </Modal>

      {/* Create/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={selectedItem ? 'Modifica lead' : 'Nuovo lead'} size="2xl" data-id="element-3857" data-mp="3idgrxk33npa1">
        <AnimatePresence mode="wait" data-id="element-3858" data-mp="3r6adfz953kwc">
          {saveSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="flex flex-col items-center justify-center py-16" data-id="element-3859" data-mp="9ygenw8iql6d">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15
          }} className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-3860" data-mp="slphxkhun07v">
                <CheckCircle2 className="w-12 h-12 text-green-500" data-id="element-3861" data-mp="9pzgpzhqq7v1" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-3862" data-mp="3rzrim1nxeohc">
                Lead salvato con successo
              </p>
            </motion.div> : <motion.div key="form" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-3863" data-mp="3i4pwzfsv04ji">
              <div className="space-y-8" data-id="element-3864" data-mp="33yxyj0uxb2h0">
                <section data-id="element-3865" data-mp="33zzbxbzew11w">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-3866" data-mp="2z6weutvfe9gy">
                    <User className="h-4 w-4" data-id="element-3867" data-mp="2u9ebrk43aogv" /> Dati lead
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3868" data-mp="2p9ryxbs6ba8l">
                    <Input label="Ragione sociale" placeholder="Azienda SPA" value={formData.name || ''} onChange={e => setFormData({
                  ...formData,
                  name: e.target.value
                })} required data-id="element-3869" data-mp="3mx0fcsdbht3q" />
                    <Input label="Referente" placeholder="Nome Cognome" value={formData.contact || ''} onChange={e => setFormData({
                  ...formData,
                  contact: e.target.value
                })} required data-id="element-3870" data-mp="21kqm7ccb5cxi" />
                    <Input label="Telefono" type="tel" value={formData.phone || ''} onChange={e => setFormData({
                  ...formData,
                  phone: e.target.value
                })} required data-id="element-3871" data-mp="lyn2ctope04s" />
                    <Input label="Email" type="email" value={formData.email || ''} onChange={e => setFormData({
                  ...formData,
                  email: e.target.value
                })} data-id="element-3872" data-mp="19qajijw5xwtv" />
                    <Select label="Provenienza" value={formData.source || ''} onChange={e => setFormData({
                  ...formData,
                  source: e.target.value
                })} options={[{
                  value: 'Web',
                  label: 'Web'
                }, {
                  value: 'Passaparola diretto',
                  label: 'Passaparola diretto'
                }, {
                  value: 'Partner',
                  label: 'Partner'
                }, {
                  value: 'Commercialista',
                  label: 'Commercialista'
                }, {
                  value: 'Altro',
                  label: 'Altro'
                }]} required data-id="element-3873" data-mp="1h7gx1cgu586a" />
                    <Select label="Stato" value={formData.status || 'Da contattare'} onChange={e => setFormData({
                  ...formData,
                  status: e.target.value
                })} options={[{
                  value: 'Da contattare',
                  label: 'Da contattare'
                }, {
                  value: 'Da ricontattare',
                  label: 'Da ricontattare'
                }, {
                  value: 'In lavorazione',
                  label: 'In lavorazione'
                }, {
                  value: 'Positivo',
                  label: 'Positivo'
                }, {
                  value: 'Negativo',
                  label: 'Negativo'
                }]} required data-id="element-3874" data-mp="2d9v0d7lxbel5" />
                    <Select label="Assegnato a" value={formData.assignedTo || ''} onChange={e => setFormData({
                  ...formData,
                  assignedTo: e.target.value
                })} options={[{
                  value: 'Alessio',
                  label: 'Alessio'
                }, {
                  value: 'Lorenzo',
                  label: 'Lorenzo'
                }, {
                  value: 'Gian Franco',
                  label: 'Gian Franco'
                }, {
                  value: 'Tiziano',
                  label: 'Tiziano'
                }]} required data-id="element-3875" data-mp="1f0h0yvc0meg1" />
                    <div className="col-span-full" data-id="element-3876" data-mp="2zgol5eedr10v">
                      <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-3877" data-mp="2zi9fb2ws8hy7">
                        Servizi di interesse
                      </label>
                      <div className="grid grid-cols-2 gap-3" data-id="element-3878" data-mp="39ecqszdixsxt">
                        {[{
                      value: 'privacy',
                      label: 'Privacy'
                    }, {
                      value: 'sicurezza',
                      label: 'Sicurezza'
                    }, {
                      value: 'certificazioni',
                      label: 'Certificazioni'
                    }, {
                      value: 'formazione',
                      label: 'Formazione'
                    }, {
                      value: 'altro',
                      label: 'Altro'
                    }].map(service => <label key={service.value} className="flex items-center gap-2 cursor-pointer" data-id="element-3879" data-mp="37sspzgn7t8kd">
                            <input type="checkbox" className="rounded border-slate-300 text-seform-orange focus:ring-seform-orange" checked={(formData.services || []).includes(service.value)} onChange={e => {
                        const current = formData.services || [];
                        if (e.target.checked) {
                          setFormData({
                            ...formData,
                            services: [...current, service.value]
                          });
                        } else {
                          setFormData({
                            ...formData,
                            services: current.filter((s: string) => s !== service.value)
                          });
                        }
                      }} data-id="element-3880" data-mp="2g2ebpjbahu86" />
                            <span className="text-sm text-slate-700" data-id="element-3881" data-mp="35xrtovpnmzgt">
                              {service.label}
                            </span>
                          </label>)}
                      </div>
                    </div>
                    <div className="col-span-full" data-id="element-3882" data-mp="2w7oo7w1cjt1l">
                      <Textarea label="Note" value={formData.notes || ''} onChange={e => setFormData({
                    ...formData,
                    notes: e.target.value
                  })} data-id="element-3883" data-mp="316i05k0zjbzn" />
                    </div>
                  </div>
                </section>

                {/* Conditional Fields based on Status */}
                {formData.status === 'Da ricontattare' && <>
                    <div className="h-px bg-slate-100" data-id="element-3884" data-mp="ggkenwur5h47"></div>
                    <section data-id="element-3885" data-mp="geq0r2hwep9m">
                      <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-3886" data-mp="20yle2jlspw8n">
                        <CalendarClock className="h-4 w-4" data-id="element-3887" data-mp="1w0tcexvcmvr9" /> Programma
                        ricontatto
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3888" data-mp="2apz8we2sy7vg">
                        <DatePicker label="Data ricontatto" value={formData.callbackDate || ''} onChange={e => setFormData({
                    ...formData,
                    callbackDate: e.target.value
                  })} required data-id="element-3889" data-mp="25x65pumzf7lh" />
                        <Input label="Ora" type="time" value={formData.callbackTime || ''} onChange={e => setFormData({
                    ...formData,
                    callbackTime: e.target.value
                  })} required data-id="element-3890" data-mp="z97undobd4e7" />
                        <div className="col-span-full" data-id="element-3891" data-mp="1t1l1mg4aoetw">
                          <Textarea label="Nota per ricontatto" value={formData.callbackNote || ''} onChange={e => setFormData({
                      ...formData,
                      callbackNote: e.target.value
                    })} data-id="element-3892" data-mp="1xzcvhfoi2b0q" />
                        </div>
                      </div>
                    </section>
                  </>}

                {formData.status === 'In lavorazione' && <>
                    <div className="h-px bg-slate-100" data-id="element-3893" data-mp="eznbc6fdwjv5"></div>
                    <section data-id="element-3894" data-mp="f1871zdfq8ul">
                      <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-3895" data-mp="exal75zh3370">
                        <Calendar className="h-4 w-4" data-id="element-3896" data-mp="50xt2cjksxed" /> Prossimo appuntamento
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3897" data-mp="2wcpzdjbc6l">
                        <DatePicker label="Data appuntamento" value={formData.appointmentDate || ''} onChange={e => setFormData({
                    ...formData,
                    appointmentDate: e.target.value
                  })} required data-id="element-3898" data-mp="xq4xtt9mij82" />
                        <Input label="Ora" type="time" value={formData.appointmentTime || ''} onChange={e => setFormData({
                    ...formData,
                    appointmentTime: e.target.value
                  })} required data-id="element-3899" data-mp="1ghxjt3q9z46a" />
                        <div className="col-span-full" data-id="element-3900" data-mp="g1j0va93q7ra">
                          <Textarea label="Note appuntamento" value={formData.appointmentNote || ''} onChange={e => setFormData({
                      ...formData,
                      appointmentNote: e.target.value
                    })} data-id="element-3901" data-mp="13qll8pv4eeph" />
                        </div>
                      </div>
                    </section>
                  </>}

                <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-3902" data-mp="3jfdgclv60yek">
                  <Button variant="flat" color="default" onClick={() => setIsModalOpen(false)} data-id="element-3903" data-mp="3bw48wy67z9s5">
                    Annulla
                  </Button>
                  <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-3905" data-mp="3fxpqgwtp8c8o" />} data-id="element-3904" data-mp="3fziwc9whmsfm">
                    Salva lead
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>

      {/* Callback Modal */}
      <Modal isOpen={isCallbackModalOpen} onClose={() => setIsCallbackModalOpen(false)} title="Programma ricontatto" size="md" data-id="element-3906" data-mp="321c8lml968to">
        <AnimatePresence mode="wait" data-id="element-3907" data-mp="29ioskll45jzg">
          {callbackSaveSuccess ? <motion.div key="callback-success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="flex flex-col items-center justify-center py-16" data-id="element-3908" data-mp="24kdic9qwp7gp">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15
          }} className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-3909" data-mp="svctadw5rpuw">
                <CheckCircle2 className="w-12 h-12 text-green-500" data-id="element-3910" data-mp="1ckdje5eru5nu" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-3911" data-mp="17lsgfy84x7vp">
                Ricontatto programmato con successo
              </p>
            </motion.div> : <motion.div key="callback-form" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-3912" data-mp="3s83tp5800oi0">
              <div className="space-y-6" data-id="element-3913" data-mp="2cqz5zi7jsztj">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3914" data-mp="2grsrk4xiq2j9">
                  <DatePicker label="Data ricontatto" value={formData.callbackDate || ''} onChange={e => setFormData({
                ...formData,
                callbackDate: e.target.value
              })} required data-id="element-3915" data-mp="2gqhltzkiikpi" />
                  <Input label="Ora" type="time" value={formData.callbackTime || ''} onChange={e => setFormData({
                ...formData,
                callbackTime: e.target.value
              })} required data-id="element-3916" data-mp="30e7ahxx5k4ui" />
                  <div className="col-span-full" data-id="element-3917" data-mp="2dgmff97bk7fi">
                    <Textarea label="Nota per ricontatto" value={formData.callbackNote || ''} onChange={e => setFormData({
                  ...formData,
                  callbackNote: e.target.value
                })} placeholder="Motivo del ricontatto..." data-id="element-3918" data-mp="28gqbf9ymgfzy" />
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-3919" data-mp="363qg51zdpzlh">
                  <Button variant="flat" color="default" onClick={() => setIsCallbackModalOpen(false)} data-id="element-3920" data-mp="313ubd0iuy44v">
                    Annulla
                  </Button>
                  <Button onClick={handleCallbackSave} startContent={<Save className="h-4 w-4" data-id="element-3922" data-mp="3ts5cwqa725xk" />} data-id="element-3921" data-mp="3a6n3t73s0lwa">
                    Salva
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>

      {/* Upload Modal */}
      <Modal isOpen={isUploadModalOpen} onClose={() => setIsUploadModalOpen(false)} title="Importa lead da Excel" size="lg" data-id="element-3923" data-mp="3orhgfp374wvy">
        <div className="space-y-6" data-id="element-3924" data-mp="3ay9mvrvk1rbm">
          <div className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center hover:bg-slate-50 transition-colors cursor-pointer" data-id="element-3925" data-mp="3frkw1znuak6p">
            <Upload className="h-10 w-10 text-slate-400 mx-auto mb-4" data-id="element-3926" data-mp="313ztc6uvmmzb" />
            <p className="text-sm text-slate-600 font-medium" data-id="element-3927" data-mp="362b3kip32zi2">
              Trascina qui il file Excel o clicca per selezionare
            </p>
            <p className="text-xs text-slate-400 mt-2" data-id="element-3928" data-mp="2s3l3cu6ywjph">
              Formati supportati: .xlsx, .csv
            </p>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg border border-blue-100" data-id="element-3929" data-mp="2p0eyzoyzwcnn">
            <h5 className="text-sm font-bold text-blue-800 mb-2" data-id="element-3930" data-mp="2k0smxiuwl2gf">
              Campi richiesti nel file:
            </h5>
            <ul className="list-disc list-inside text-xs text-blue-700 space-y-1" data-id="element-3931" data-mp="2f9kasf26v2zk">
              <li data-id="element-3932" data-mp="2b8qotr8b3y9b">Ragione sociale</li>
              <li data-id="element-3933" data-mp="2b7pddlndp9r2">Telefono</li>
              <li data-id="element-3934" data-mp="2b6ebjrdhuc2l">Referente</li>
              <li data-id="element-3935" data-mp="2694epqnryy4q">Provenienza</li>
            </ul>
            <div className="mt-3 text-xs text-blue-600" data-id="element-3936" data-mp="26ayxozfhkg66">
              <a href="#" className="underline" data-id="element-3937" data-mp="219rpx0dcfgzi">
                Scarica template di esempio
              </a>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4" data-id="element-3938" data-mp="1wbhv4qvj6ief">
            <Button variant="flat" color="default" onClick={() => setIsUploadModalOpen(false)} data-id="element-3939" data-mp="1sa4r8mc00pa5">
              Annulla
            </Button>
            <Button onClick={handleUpload} data-id="element-3940" data-mp="3mn4b4rnew5n8">Importa file</Button>
          </div>
        </div>
      </Modal>

      {/* Blacklist Modal */}
      <Modal isOpen={isBlacklistModalOpen} onClose={() => setIsBlacklistModalOpen(false)} title="Elimina e aggiungi a blacklist" size="md" data-id="element-3941" data-mp="18fd5i0v9yowt">
        <div className="space-y-6" data-id="element-3942" data-mp="1i6qxzoot6ogg">
          <div className="bg-red-50 p-4 rounded-lg border border-red-100 flex gap-3" data-id="element-3943" data-mp="1i7sn2x2ntbh6">
            <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" data-id="element-3944" data-mp="1m6rldytvy9zn" />
            <p className="text-sm text-red-700" data-id="element-3945" data-mp="pi25ge1tjrgb">
              Attenzione: il lead verrà rimosso dalla pipeline e aggiunto alla
              blacklist. Non sarà più possibile importarlo o crearlo manualmente
              con questo numero di telefono.
            </p>
          </div>

          <Select label="Motivazione eliminazione" options={[{
          value: 'not_interested',
          label: 'Non interessato'
        }, {
          value: 'no_answer',
          label: 'Non ha mai risposto'
        }, {
          value: 'unreachable',
          label: 'Impossibile parlare con referente'
        }, {
          value: 'other',
          label: 'Altro'
        }]} required data-id="element-3946" data-mp="13h1uhmt8u8d0" />

          <Textarea label="Note aggiuntive" placeholder="Dettagli..." data-id="element-3947" data-mp="jsaf7o0ik6x9" />

          <div className="flex justify-end gap-3 pt-4" data-id="element-3948" data-mp="3s8llqhqwz0ds">
            <Button variant="flat" color="default" onClick={() => setIsBlacklistModalOpen(false)} data-id="element-3949" data-mp="3s9dg5p8rys2h">
              Annulla
            </Button>
            <Button color="danger" onClick={confirmBlacklist} data-id="element-3950" data-mp="5tssntmch0gx">
              Conferma eliminazione
            </Button>
          </div>
        </div>
      </Modal>
    </Layout>;
}